import React, { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { UploadCloud, File, X, CheckCircle, AlertCircle, Shield, User, Tag } from 'lucide-react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

const UploadPage = () => {
    const { token } = useAuth();
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);
    const [members, setMembers] = useState([]);

    // Form State
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('Personal & Identity');
    const [subCategory, setSubCategory] = useState('');
    const [relatedPerson, setRelatedPerson] = useState('');
    const [expiryDate, setExpiryDate] = useState('');

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const res = await axios.get('http://localhost:4000/api/auth/members', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMembers(res.data.data.members || []);
            } catch (error) {
                console.error("Failed to fetch family members");
            }
        };
        fetchMembers();
    }, [token]);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragging(true);
        } else if (e.type === 'dragleave') {
            setDragging(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFile(e.dataTransfer.files[0]);
            setTitle(e.dataTransfer.files[0].name);
        }
    }, []);

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setTitle(e.target.files[0].name);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) return;

        setUploading(true);
        setMessage(null);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', title);
        formData.append('category', category);
        if (subCategory) formData.append('sub_category', subCategory);
        if (relatedPerson) formData.append('related_person_name', relatedPerson);
        if (expiryDate) formData.append('expiry_date', expiryDate);

        try {
            await axios.post('http://localhost:4000/api/documents', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
            });
            setMessage({ type: 'success', text: 'Document uploaded & encrypted successfully!' });
            setFile(null);
            setTitle('');
            setSubCategory('');
            setRelatedPerson('');
            setExpiryDate('');
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Upload failed' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-8">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Secure Upload</h1>
                    <p className="text-gray-500">Files are automatically encrypted with AES-256 upon upload.</p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-8 border border-gray-100 dark:border-gray-700 relative overflow-hidden"
                >
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                        <Shield className="w-64 h-64" />
                    </div>

                    {/* Drag & Drop Zone */}
                    <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={clsx(
                            "relative border-3 border-dashed rounded-xl p-16 text-center transition-all duration-300 cursor-pointer group",
                            dragging
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/10 scale-[1.02]"
                                : "border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-900/50",
                            file ? "border-green-500 bg-green-50 dark:bg-green-900/10" : ""
                        )}
                    >
                        <input
                            type="file"
                            className="hidden"
                            id="file-upload"
                            onChange={handleChange}
                            accept=".pdf,.png,.jpg,.jpeg"
                        />

                        {!file ? (
                            <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                                <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                                    <UploadCloud className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                                </div>
                                <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                    Click or Drag file to upload
                                </p>
                                <p className="text-sm text-gray-500">PDF, PNG, JPG up to 10MB</p>
                            </label>
                        ) : (
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="relative flex flex-col items-center"
                            >
                                <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                                    <File className="h-10 w-10 text-green-600 dark:text-green-400" />
                                </div>
                                <p className="text-xl font-medium text-gray-900 dark:text-white truncate max-w-sm">{file.name}</p>
                                <p className="text-sm text-gray-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFile(null); }}
                                    className="mt-6 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center text-sm font-medium"
                                >
                                    <X className="h-4 w-4 mr-2" />
                                    Remove File
                                </button>
                            </motion.div>
                        )}
                    </div>

                    {/* Metadata Form */}
                    <AnimatePresence>
                        {file && (
                            <motion.form
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                onSubmit={handleUpload}
                                className="mt-8 space-y-6 overflow-hidden"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Document Title</label>
                                        <input
                                            type="text"
                                            required
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            className="w-full rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                                            placeholder="e.g. Insurance Policy 2024"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Category</label>
                                        <select
                                            value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                            className="w-full rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                                        >
                                            <option>Personal & Identity</option>
                                            <option>Financial Records</option>
                                            <option>Insurance</option>
                                            <option>Medical</option>
                                            <option>Property</option>
                                            <option>Legal</option>
                                            <option>Other</option>
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Sub-Category</label>
                                        <div className="relative">
                                            <Tag className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                            <input
                                                type="text"
                                                value={subCategory}
                                                onChange={(e) => setSubCategory(e.target.value)}
                                                className="w-full pl-10 rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                                                placeholder="e.g. Vaccination"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Related Person</label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                            <input 
                                                list="members-list"
                                                type="text"
                                                value={relatedPerson}
                                                onChange={(e) => setRelatedPerson(e.target.value)}
                                                 className="w-full pl-10 rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                                                placeholder="Select or type name"
                                            />
                                            <datalist id="members-list">
                                                {members.map(m => (
                                                    <option key={m._id} value={m.email.split('@')[0]} />
                                                ))}
                                            </datalist>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Expiry Date</label>
                                        <input
                                            type="date"
                                            value={expiryDate}
                                            onChange={(e) => setExpiryDate(e.target.value)}
                                            className="w-full rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                                        />
                                    </div>
                                </div>

                                {message && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={clsx("p-4 rounded-xl flex items-center shadow-sm", message.type === 'success' ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200")}
                                    >
                                        {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-3 shrink-0" /> : <AlertCircle className="h-5 w-5 mr-3 shrink-0" />}
                                        <span className="font-medium">{message.text}</span>
                                    </motion.div>
                                )}

                                <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <button
                                        type="submit"
                                        disabled={uploading}
                                        className="inline-flex items-center justify-center py-3.5 px-8 border border-transparent shadow-lg text-sm font-bold rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all hover:scale-[1.02] active:scale-95"
                                    >
                                        {uploading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Encrypting & Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Shield className="w-5 h-5 mr-2" />
                                                Secure Upload
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </Layout>
    );
};

export default UploadPage;
